'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackTouchpoint } from '@/lib/attribution/client'
import { hasFirstTouchMarketingSignals } from '@/lib/attribution/storage'
import { ATTRIBUTION_ALLOWLIST_PATH_PREFIXES } from '@/lib/attribution/types'
import { ATTRIBUTION_ENABLED } from '@/lib/config'
import { attributionDebug } from '@/lib/attribution/logger'

function isPathAllowlisted(pathname: string): boolean {
  return ATTRIBUTION_ALLOWLIST_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function useAttributionTracking() {
  const pathname = usePathname()
  const hasTrackedInitialPageviewRef = useRef(false)

  useEffect(() => {
    if (!pathname) return

    if (!ATTRIBUTION_ENABLED) {
      attributionDebug('Skipping route tracking because attribution is disabled', {
        pathname,
      })
      return
    }

    if (!hasTrackedInitialPageviewRef.current) {
      hasTrackedInitialPageviewRef.current = true
      const allowlisted = isPathAllowlisted(pathname)
      const hasFirstTouchSignals = hasFirstTouchMarketingSignals()

      attributionDebug('Evaluated initial page tracking conditions', {
        pathname,
        allowlisted,
        hasFirstTouchSignals,
      })

      if (allowlisted || hasFirstTouchSignals) {
        attributionDebug('Tracking initial pageview', {
          pathname,
          reason: allowlisted ? 'allowlisted-path' : 'first-touch-signals',
        })
        void trackTouchpoint('pageview')
      } else {
        attributionDebug('Skipping initial pageview (not allowlisted and no first-touch signals)', {
          pathname,
        })
      }
      return
    }

    if (!isPathAllowlisted(pathname)) {
      attributionDebug('Skipping pathname-change pageview (path not allowlisted)', {
        pathname,
      })
      return
    }

    attributionDebug('Tracking pathname-change pageview', {
      pathname,
    })
    void trackTouchpoint('pageview')
  }, [pathname])
}
